import apiClient from './apiClient';

/**
 * Add a symbol to the user's watchlist
 * @param {string} symbol - Canonical platform symbol (e.g., 'BTC/USD', 'AAPL')
 * @returns {Promise<Object>} Watchlist response
 */
export const addToWatchlist = async (symbol) => {
  const response = await apiClient.post('/watchlist', { symbol });
  return response.data;
};

/**
 * Complete onboarding by:
 * 1. Populating watchlist with selected symbols
 * 2. Sending preferences to backend to enable template events
 * 3. Setting onboardingComplete flag
 * 
 * @param {Object} preferences - User's onboarding selections
 * @param {string[]} preferences.markets - Selected markets (e.g., ['crypto', 'stocks'])
 * @param {string[]} preferences.symbols - Canonical asset symbols (e.g., ['BTC/USD', 'AAPL'])
 * @param {string[]} preferences.signalTypes - Selected signal types (e.g., ['momentum', 'trend'])
 * @returns {Promise<Object>} Updated user settings
 */
export const completeOnboarding = async (preferences) => {
  const { symbols, signalTypes } = preferences;

  // Default symbols if none selected — canonical platform symbols only;
  // anything the SymbolsTable doesn't serve becomes a dead watchlist tile.
  const watchlistSymbols = symbols && symbols.length > 0
    ? symbols
    : ['BTC/USD', 'ETH/USD', 'AAPL', 'NVDA', 'TSLA', 'MSFT', 'XAU/USD', 'SPY'];
  
  // Step 1: Populate watchlist
  // Use Promise.allSettled to continue even if some symbols fail
  const watchlistResults = await Promise.allSettled(
    watchlistSymbols.map(symbol => addToWatchlist(symbol))
  );
  
  // Log any failures but don't block completion
  const failedSymbols = watchlistResults
    .map((result, index) => ({ ...result, originalIndex: index }))
    .filter(result => result.status === 'rejected')
    .map(result => watchlistSymbols[result.originalIndex]);
  
  if (failedSymbols.length > 0) {
    console.warn('Failed to add some symbols to watchlist:', failedSymbols);
  }
  
  // Step 2: Complete onboarding (enables template events based on signal preferences)
  // Backend expects { preferences: ['momentum', 'trend', 'sr'] }
  const response = await apiClient.post('/user/onboarding/complete', {
    preferences: signalTypes || []
  });

  return response.data;
};

/**
 * Skip onboarding without provisioning a watchlist or enabling any template
 * events. Calls the same backend endpoint but with an empty preferences array,
 * which the backend treats as a no-op completion (it still flips the
 * `onboardingCompleted` flag, so the wizard won't reappear).
 *
 * Used by the wizard's "Skip for now" affordance and the X close button —
 * without these, users with `onboardingCompleted: false` are trapped on
 * every page with no escape.
 *
 * @returns {Promise<Object>} Backend response
 */
export const skipOnboarding = async () => {
  const response = await apiClient.post('/user/onboarding/complete', {
    preferences: []
  });
  return response.data;
};
